// Money arrives from the API as decimal strings with up to four places
// ("1234.5000"). We never turn it into a float: amounts are held as bigint
// ten-thousandths, added exactly, and rounded half away from zero only for
// display — the same rounding the ledger uses.

const SCALE = 4;
const FACTOR = 10n ** BigInt(SCALE);
const MONEY_RE = /^([+-]?)(\d+)(?:\.(\d{1,4}))?$/;

/** Parses a decimal string into ten-thousandths. Throws on anything else. */
export function toUnits(value: string): bigint {
  const s = String(value).trim().replace(/−/g, '-').replace(/,/g, '');
  const m = MONEY_RE.exec(s);
  if (!m) throw new RangeError(`Not a money amount: "${value}"`);
  const [, sign, whole, frac = ''] = m;
  const units = BigInt(whole!) * FACTOR + BigInt(frac.padEnd(SCALE, '0'));
  return sign === '-' ? -units : units;
}

/** Formats ten-thousandths back to the API's four-place string. */
export function fromUnits(units: bigint): string {
  const neg = units < 0n;
  const abs = neg ? -units : units;
  const whole = abs / FACTOR;
  const frac = (abs % FACTOR).toString().padStart(SCALE, '0');
  return `${neg ? '-' : ''}${whole}.${frac}`;
}

export function addMoney(...values: string[]): string {
  return fromUnits(values.reduce((t, v) => t + toUnits(v), 0n));
}

export function negate(value: string): string {
  return fromUnits(-toUnits(value));
}

export function isZero(value: string): boolean {
  return toUnits(value) === 0n;
}

export function compareMoney(a: string, b: string): -1 | 0 | 1 {
  const d = toUnits(a) - toUnits(b);
  return d < 0n ? -1 : d > 0n ? 1 : 0;
}

/** Rounds to `places` decimals, half away from zero. */
export function roundUnits(units: bigint, places: number): bigint {
  const step = 10n ** BigInt(SCALE - places);
  const neg = units < 0n;
  const abs = neg ? -units : units;
  const r = ((abs + step / 2n) / step) * step;
  return neg ? -r : r;
}

/** Parses any decimal string (up to `scale` places) into scaled bigint units. */
function scaled(value: string, scale: number): bigint {
  const s = String(value).trim().replace(/\u2212/g, '-').replace(/,/g, '');
  const m = /^([+-]?)(\d+)(?:\.(\d+))?$/.exec(s);
  if (!m) throw new RangeError(`Not a number: "${value}"`);
  const [, sign, whole, frac = ''] = m;
  if (frac.length > scale) throw new RangeError(`Too many decimal places: "${value}"`);
  const units = BigInt(whole!) * 10n ** BigInt(scale) + BigInt(frac.padEnd(scale, '0') || '0');
  return sign === '-' ? -units : units;
}

/**
 * a × b, rounded half away from zero to `places` (default cents), returned
 * as a four-place money string. Exact: quantity 3 × 33.33, or a net amount ×
 * a VAT rate of 0.135, never touches floating point.
 */
export function mulMoney(a: string, b: string, places = 2): string {
  const A = scaled(a, 6);
  const B = scaled(b, 6);
  const product = A * B; // scale 12
  const step = 10n ** BigInt(12 - places);
  const neg = product < 0n;
  const abs = neg ? -product : product;
  const rounded = ((abs + step / 2n) / step) * step;
  const toScale4 = rounded / 10n ** 8n; // scale 12 → 4
  return fromUnits(neg ? -toScale4 : toScale4);
}

/**
 * `ratePercent` per cent of an amount ("13.5" → 13.5%), rounded half away
 * from zero to `places` decimals: the ledger's `round(net * rate, 2)`.
 */
export function percentOf(amount: string, ratePercent: string, places = 2): string {
  const n = toUnits(amount) * scaled(ratePercent, 4); // scale 4 × percent at scale 4
  const den = 10n ** 6n * 10n ** BigInt(SCALE - places); // ÷100 for per cent, ÷10^4 for the rate's scale
  const neg = n < 0n;
  const abs = neg ? -n : n;
  const q = (2n * abs + den) / (2n * den);
  const units = q * 10n ** BigInt(SCALE - places);
  return fromUnits(neg ? -units : units);
}

/** Whole currency units, as ROS wants VAT3 figures. */
export function wholeUnits(value: string): number {
  return Number(roundUnits(toUnits(value), 0) / FACTOR);
}

const SYMBOL: Record<string, string> = { EUR: '€', GBP: '£', USD: '$' };
/** Currencies without minor units (ISO 4217): shown in whole amounts unless asked otherwise. */
const WHOLE = new Set(['JPY', 'KRW', 'ISK', 'VND', 'CLP']);

export interface FormatOptions {
  /** Show "+" on positive amounts (for movements). */
  signed?: boolean;
  /** Decimal places to show; defaults to 2. */
  places?: 0 | 2;
  /** Leave out the currency symbol (tables with a currency column). */
  bare?: boolean;
}

/**
 * Formats for display: "€1,234.50", "−€820.00" (a true minus sign, so
 * columns of figures line up in tabular numerals).
 */
export function formatMoney(value: string, currency = 'EUR', opts: FormatOptions = {}): string {
  const places = opts.places ?? (WHOLE.has(currency) ? 0 : 2);
  const units = roundUnits(toUnits(value), places);
  const neg = units < 0n;
  const abs = neg ? -units : units;
  const whole = (abs / FACTOR).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  const frac = places === 0 ? '' : '.' + (abs % FACTOR).toString().padStart(SCALE, '0').slice(0, places);
  const symbol = opts.bare ? '' : (SYMBOL[currency] ?? `${currency} `);
  const sign = neg ? '−' : opts.signed && units > 0n ? '+' : '';
  return `${sign}${symbol}${whole}${frac}`;
}
